import VueI18n, { TranslateResult } from 'vue-i18n';
import SFormElement from './SFormElement.vue';
import set from 'lodash/set';
import unset from 'lodash/unset';
import get from 'lodash/get';
import remove from 'lodash/remove';
import flatMap from 'lodash/flatMap';
import compact from 'lodash/compact';
import debounce from 'lodash/debounce';

export interface ISingleElementValidationData {
  /**
   * dataValue is the value of the data bound with v-model.
   */
  dataValue: any; // eslint-disable-line @typescript-eslint/no-explicit-any

  /**
   * objectValue is the value of the object in the component.
   * It will usually be falsy, but for components like s-autocomplete,
   * which contain data other than what is bound with v-model,
   * this will be the selected object.
   */
  objectValue: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface INestedValidationData extends ISingleElementValidationData {
  [key: string]: INestedValidationData;
}

interface IFullValidationData extends ISingleElementValidationData {
  formElements: INestedValidationData;
  uid: string | undefined;
}

type RuleType = 'error' | 'warning';

// examples:
// 'customerParty.name'
// { pathCombiner } => combiner('orderLine.quantity', uid)
// { withSameUID } => withSameUID('orderLine.quantity')
type RuleTriggerBy = string | (
  (helpers: {
    pathCombiner: (validationPath: string, validationUID?: string) => string;
    withSameUID: (validationPath: string) => string;
  }) => string);

export type sValidation = (data: IFullValidationData) => boolean;
export type sValidationCondition = boolean | ((data: IFullValidationData) => boolean);

export interface IRule {
  readonly message?: string | TranslateResult | ((i18n: VueI18n) => string | TranslateResult);
  readonly validation: sValidation;
  readonly triggerBy?: RuleTriggerBy[];
  readonly if?: sValidationCondition;
  readonly type?: RuleType;
}

export interface IRules {
  readonly[key: string]: IRule[] | IRules;
}

export interface IElementRuleDatum {
  readonly rule: IRule;
  valid: boolean;
  hasWarning: boolean;
}

function getRulesFromPath(rules: IRules, path: string): IRule[] {
  const pathPieces = path.split('.');
  if (!pathPieces.length) return [];
  let currentNode = rules;

  let foundRules: IRule[] | undefined;

  pathPieces.forEach(pathPiece => {
    if (foundRules) return;

    if (Array.isArray(currentNode[pathPiece])) {
      foundRules = currentNode[pathPiece] as IRule[];
    } else if (!currentNode[pathPiece]) {
      console.warn( // eslint-disable-line no-console
        `Element is trying to use rule '${path}', which doesn't exist. ` +
        `Validations for this element will be ignored.`,
      );
      foundRules = [] as IRule[];
    } else {
      currentNode = currentNode[pathPiece] as IRules;
    }
  });

  return foundRules as IRule[];
}

export class ElementValidation {
  private rulesData: IElementRuleDatum[]
  public valid = true;
  public hasWarning = false;
  public readonly formElementsPath: string;
  public readonly formElementsPathSplit: string[];
  public readonly allTriggerBy: string[];

  private static pathCombiner(validationPath: string, validationUID?: string): string {
    return validationPath + (validationUID ? `.${validationUID}` : '');
  }

  constructor(
    private readonly el: SFormElement,
    public readonly dataGetter: () => ISingleElementValidationData,
    public readonly validationGroup: ValidationGroup,
    public readonly validationPath: string,
    rules: IRules,
    private readonly validationUID?: string,
  ) {
    this.rulesData = getRulesFromPath(rules, validationPath).map(rule => ({ rule, valid: true, hasWarning: false }));
    this.formElementsPath = ElementValidation.pathCombiner(this.validationPath, this.validationUID);
    this.formElementsPathSplit = this.formElementsPath.split('.');
    this.allTriggerBy = compact(flatMap(this.rulesData.map(ruleDatum => {
      if (!ruleDatum.rule.triggerBy) return;

      return ruleDatum.rule.triggerBy.map(triggerBy =>
        typeof triggerBy === 'function'
          ? triggerBy({
            pathCombiner: ElementValidation.pathCombiner,
            withSameUID: validationPath => ElementValidation.pathCombiner(validationPath, (this.validationUID as string)),
          })
          : triggerBy,
      );
    })));
    this.validationGroup.addElementValidation(this);
  }

  private validateRule(ruleDatum: IElementRuleDatum): boolean {
    ruleDatum.valid = true;
    ruleDatum.hasWarning = false;

    if (this.shouldValidate(ruleDatum)) {
      const valid = ruleDatum.rule.validation({
        ...this.dataGetter(),
        formElements: this.validationGroup.formElements,
        uid: this.validationUID,
      } as IFullValidationData);

      if (!valid) {
        if (ruleDatum.rule.type === undefined || ruleDatum.rule.type === 'error') {
          ruleDatum.valid = false;
        } else if (ruleDatum.rule.type === 'warning') {
          ruleDatum.hasWarning = true;
        }
      }
    }

    return ruleDatum.valid;
  }

  private shouldValidate(ruleDatum: IElementRuleDatum): boolean {
    if (typeof ruleDatum.rule.if === 'function') {
      return ruleDatum.rule.if({
        ...this.dataGetter(),
        formElements: this.validationGroup.formElements,
        uid: this.validationUID,
      } as IFullValidationData);
    }

    if (typeof ruleDatum.rule.if === 'boolean') {
      return ruleDatum.rule.if;
    }

    return true;
  }

  public validate(): void {
    this.rulesData.forEach(ruleDatum => this.validateRule(ruleDatum));
    this.valid = this.rulesData.every(ruleDatum => ruleDatum.valid);
    this.hasWarning = this.rulesData.some(ruleDatum => ruleDatum.hasWarning);
    this.validationGroup.validate();
  }

  public get errorMessages(): (string | TranslateResult)[] {
    return this
      .rulesData
      .filter(ruleDatum => (!ruleDatum.valid || ruleDatum.hasWarning) && ruleDatum.rule.message)
      .map(ruleDatum =>
        typeof ruleDatum.rule.message === 'function'
          ? ruleDatum.rule.message(this.el.$i18n)
          : ruleDatum.rule.message as string,
      );
  }

  public get elementsToTrigger(): SFormElement[] {
    return this.validationGroup
      .elementValidationsTriggeredBy(this)
      .map(elementValidation => elementValidation.el);
  }
}

export class ValidationGroup {
  private els: ElementValidation[] = [];
  public formElements: INestedValidationData = {} as INestedValidationData;
  public valid = true;
  public validate = debounce(this.validateDelayed, 50);

  public addElementValidation(elementValidation: ElementValidation): void {
    this.els.push(elementValidation);

    if (get(this.formElements, elementValidation.formElementsPathSplit)) {
      console.warn( // eslint-disable-line no-console
        `Multiple elements use the rule '${elementValidation.formElementsPath}'. ` +
        `Please use prop ':validation-uid' to differentiate between the elements.`,
      );
    }

    this.updateFormElemement(elementValidation);
  }

  public updateFormElemement(elementValidation: ElementValidation): void {
    set(
      this.formElements,
      elementValidation.formElementsPathSplit,
      elementValidation.dataGetter(),
    );
  }

  public removeElementValidation(elementValidation: ElementValidation): void {
    remove(this.els, el => el === elementValidation);
    unset(this.formElements, elementValidation.formElementsPathSplit);
    this.validate();
  }

  private validateDelayed(): void {
    this.valid = this.els.every(elementValidation => elementValidation.valid);
  }

  public elementValidationsTriggeredBy(triggerer: ElementValidation): ElementValidation[] {
    return this.els.filter(elementValidation =>
      elementValidation.allTriggerBy.some(triggerBy =>
        triggerBy === triggerer.formElementsPath ||
        triggerBy === triggerer.validationPath,
      ),
    );
  }
}
